import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { presentationStyles } from './presentationStyle';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { apiGet } from '../utilities/apiClient';
import { AppearanceContext } from './AppearanceContext';
import { PresentationContext, PresentationPendingContext, type PresentationSnapshot } from './PresentationContext';
import { cachePresentation, defaultPresentation, readPresentation, readPresentationCache } from './presentationCache';
import AppLoadingFallback from '../components/AppLoadingFallback';
import { resolveScreenDefaults } from '../screenBuilder/model';

export default function AppearanceProvider({ children }: { children: React.ReactNode }) {
  const authorized = useSelector((state: RootState) => state.session.authorized);
  const accountId = useSelector((state: RootState) => state.session.account?.account_id);
  const flags = useSelector((state: RootState) => state.featureFlags);
  const userId = useSelector((state: RootState) => state.authentication.user?.cognito_id);
  const key = authorized && accountId && userId ? JSON.stringify([accountId, userId]) : 'signed-out';
  const [active, setActive] = useState<{ key: string; snapshot: PresentationSnapshot; resolvedAt: number } | null>(null);
  useEffect(() => {
    if (active?.key === key) return;
    let current = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      let snapshot: PresentationSnapshot;
      let resolvedAt = Date.now();
      if (key === 'signed-out') {
        snapshot = defaultPresentation();
      } else {
        let enabled = false;
        try {
          // Bound startup. Once selected, a late response cannot restyle this session.
          const response = await Promise.race([
            apiGet(`/users/app-screens/${accountId}/${userId}?catalog=customer-elements-v1`),
            new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('Presentation load timed out')), 10000); }),
          ]);
          resolvedAt = Date.now();
          enabled = response?.success === true && response.appStudioEnabled === true;
          if (response?.success && !enabled) {
            snapshot = defaultPresentation();
          } else {
            const validated = response?.success && readPresentation({ version: 1, appearance: response.appearance, screens: response.payload });
            if (!validated) throw new Error('Presentation unavailable');
            snapshot = { ...validated, screens: resolveScreenDefaults(defaultPresentation().screens, validated.screens) };
          }
          if (current) void cachePresentation(snapshot);
        } catch {
          // A stored design cannot establish that the global gate is still on.
          snapshot = enabled ? await readPresentationCache() || defaultPresentation() : defaultPresentation();
        }
        finally { if (timer) clearTimeout(timer); }
      }
      if (current) setActive({ key, snapshot, resolvedAt });
    };
    void load();
    return () => { current = false; if (timer) clearTimeout(timer); };
  }, [key]);
  // Compare identities during render, before effects run, so an account switch
  // cannot display even one frame using the preceding account's snapshot.
  // Keep bootstrap/navigation mounted so pending sign-in links are not lost.
  if (!active) return <AppLoadingFallback />;
  const pending = active.key !== key;
  // Older Redux flags cannot override the gate returned with this snapshot.
  // A later foreground refresh can still switch the current session off.
  const studioDisabled = flags?.fetched && (flags.fetchedAt ?? 0) > active.resolvedAt && flags.flags?.app_studio_enabled !== true;
  const snapshot = studioDisabled ? defaultPresentation() : active.snapshot;
  return <PresentationContext.Provider value={snapshot}><PresentationPendingContext.Provider value={pending}><AppearanceContext.Provider value={snapshot.appearance}>
    <View style={presentationStyles.root}>
      <View style={[presentationStyles.content, pending && presentationStyles.hidden]} pointerEvents={pending ? 'none' : 'auto'} accessibilityElementsHidden={pending} importantForAccessibility={pending ? 'no-hide-descendants' : 'auto'}>{children}</View>
      {pending && <AppLoadingFallback />}
    </View>
  </AppearanceContext.Provider></PresentationPendingContext.Provider></PresentationContext.Provider>;
}
